import React from 'react'
import { Typography, Button, Card, Badge, Empty, Tag } from 'antd'
import { useSelector } from 'react-redux'
import {
  RightOutlined,
  CalendarOutlined,
  CheckCircleFilled,
  WarningFilled,
  InfoCircleFilled,
  ArrowUpOutlined,
  ArrowDownOutlined
} from '@ant-design/icons'
import '../../../styling/RecentResults.css'

const { Text, Title } = Typography

const RecentResults = ({ results = [], onViewDetails }) => {
  const testTemplates = useSelector((state) => state.careSyncTests.data)
  const testResults = useSelector((state) => state.careSyncTestResults.data)
  const dataPoints = useSelector((state) => state.careSyncDataPoints.data)

  // Get both actual and mock results
  const realResults = testResults
    ? [...testResults].sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated)).slice(0, 3)
    : []

  const mockResults = results || []

  // Combine all results - real ones come first
  const allResults = [
    ...realResults.map((r) => ({
      id: r._id,
      isReal: true,
      result: r
    })),
    ...mockResults.map((r) => ({
      id: r.id,
      isReal: false,
      result: r
    }))
  ].slice(0, 3) // Show max 3 results

  // Helper function to determine result status for real results
  const getResultStatus = (result, template) => {
    if (!result || !template) return 'unknown'

    try {
      const referenceRanges = JSON.parse(template?.data || '{}').referenceRangeData || []
      if (referenceRanges.length === 0) return 'unknown'

      const value = parseFloat(result.value)
      let isNormal = false

      for (const range of referenceRanges) {
        const min = range.min !== undefined ? parseFloat(range.min) : null
        const max = range.max !== undefined ? parseFloat(range.max) : null

        if ((min === null || value >= min) && (max === null || value <= max)) {
          isNormal = true
          break
        }
      }

      return isNormal ? 'normal' : 'abnormal'
    } catch (e) {
      return 'unknown'
    }
  }

  // Format date helper
  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  // Render a real result from Redux store
  const renderRealResult = (result) => {
    const template = testTemplates?.find((t) => t.key === result.key)
    const dataPoint = dataPoints?.find((dp) => dp.key === result.key)

    if (!template && !dataPoint) return null

    const status = getResultStatus(result, template)
    const referenceRanges = template?.data ? JSON.parse(template.data).referenceRangeData || [] : []

    return (
      <Card
        key={result._id}
        className={`cs-result-card cs-result-card--${status}`}
        onClick={() => onViewDetails(result)}
      >
        <div className='cs-result-card-content'>
          <div className='cs-result-name-row'>
            <Text className='cs-result-name'>{template?.name || dataPoint?.label || 'Test Result'}</Text>
            {status === 'normal' ? (
              <Badge status='success' text={<Text className='cs-status-badge cs-status-badge--normal'>Normal</Text>} />
            ) : status === 'abnormal' ? (
              <Badge
                status='error'
                text={<Text className='cs-status-badge cs-status-badge--abnormal'>Abnormal</Text>}
              />
            ) : (
              <Badge status='default' text={<Text className='cs-status-badge'>Unspecified</Text>} />
            )}
          </div>

          <div className='cs-result-value-row'>
            <Text className='cs-result-value'>
              {result.value} {template?.suffix || dataPoint?.suffix || ''}
            </Text>

            <div className='cs-result-meta'>
              <Text className='cs-result-date'>
                <CalendarOutlined /> {formatDate(result.dateCreated)}
              </Text>
            </div>
          </div>

          {referenceRanges.length > 0 && (
            <Text className='cs-result-reference'>Reference: {referenceRanges[0].label}</Text>
          )}

          <div className='cs-result-status-icon'>
            {status === 'normal' ? (
              <CheckCircleFilled className='cs-status-icon cs-status-icon--normal' />
            ) : status === 'abnormal' ? (
              <WarningFilled className='cs-status-icon cs-status-icon--abnormal' />
            ) : (
              <InfoCircleFilled className='cs-status-icon' />
            )}
          </div>
        </div>
      </Card>
    )
  }

  // Render a mock result from hardcoded data
  const renderMockResult = (result) => {
    const status = result.status || 'normal'
    const trend = result.metrics?.change?.trend

    return (
      <Card
        key={result.id}
        className={`cs-result-card cs-result-card--${status === 'normal' ? 'normal' : 'abnormal'}`}
        onClick={() => onViewDetails(result)}
      >
        <div className='cs-result-card-content'>
          <div className='cs-result-name-row'>
            <Text className='cs-result-name'>{result.testName}</Text>
            {status === 'normal' ? (
              <Badge status='success' text={<Text className='cs-status-badge cs-status-badge--normal'>Normal</Text>} />
            ) : (
              <Badge
                status='error'
                text={<Text className='cs-status-badge cs-status-badge--abnormal'>Attention</Text>}
              />
            )}
          </div>

          <div className='cs-result-value-row'>
            <div className='cs-result-value-container'>
              <Text className='cs-result-value'>
                {result.metrics.current.value} {result.metrics.current.unit}
              </Text>

              {trend && (
                <Tag
                  className={`cs-trend-tag cs-trend-tag--${trend}`}
                  icon={trend === 'improving' ? <ArrowDownOutlined /> : <ArrowUpOutlined />}
                >
                  {Math.abs(result.metrics.change.value)}%
                </Tag>
              )}
            </div>

            <div className='cs-result-meta'>
              <Text className='cs-result-date'>
                <CalendarOutlined /> {result.date}
              </Text>
            </div>
          </div>

          <Text className='cs-result-reference'>
            Reference: {result.metrics.referenceRange.min} - {result.metrics.referenceRange.max}{' '}
            {result.metrics.referenceRange.unit}
          </Text>

          <div className='cs-result-status-icon'>
            {status === 'normal' ? (
              <CheckCircleFilled className='cs-status-icon cs-status-icon--normal' />
            ) : (
              <WarningFilled className='cs-status-icon cs-status-icon--abnormal' />
            )}
          </div>
        </div>
      </Card>
    )
  }

  return (
    <div className='cs-recent-results'>
      <div className='cs-results-header'>
        <Title level={5}>Recent Test Results</Title>
        <Button type='link' className='cs-view-all-btn' onClick={() => onViewDetails('viewAll')}>
          View All <RightOutlined />
        </Button>
      </div>

      <div className='cs-results-content'>
        {allResults.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description='No test results available yet'
            className='cs-no-results'
          />
        ) : (
          allResults.map((item) => (item.isReal ? renderRealResult(item.result) : renderMockResult(item.result)))
        )}
      </div>
    </div>
  )
}

export default RecentResults
